/**
 * Navigation Source Utility
 * Manages localStorage to track where users came from (family landing or member detail)
 * so they can be redirected back after completing actions on related pages
 */

const NAVIGATION_SOURCE_KEY = "navigationSource";

export type NavigationSource = {
  type: "family-landing" | "member-detail" | "overview";
  path: string;
  timestamp: number;
};

/**
 * Set the navigation source when visiting family landing, member detail, or overview page
 */
export const setNavigationSource = (
  type: "family-landing" | "member-detail" | "overview",
  path: string
): void => {
  const source: NavigationSource = {
    type,
    path,
    timestamp: Date.now(),
  };
  localStorage.setItem(NAVIGATION_SOURCE_KEY, JSON.stringify(source));
};

/**
 * Get the stored navigation source
 */
export const getNavigationSource = (): NavigationSource | null => {
  try {
    const stored = localStorage.getItem(NAVIGATION_SOURCE_KEY);
    if (!stored) return null;

    const source: NavigationSource = JSON.parse(stored);

    // Check if source is older than 1 hour (3600000 ms) - if so, clear it
    const oneHourAgo = Date.now() - 3600000;
    if (source.timestamp < oneHourAgo) {
      clearNavigationSource();
      return null;
    }

    return source;
  } catch (error) {
    console.error("Error reading navigation source:", error);
    return null;
  }
};

/**
 * Clear the navigation source (e.g., when footer navigation is clicked)
 */
export const clearNavigationSource = (): void => {
  localStorage.removeItem(NAVIGATION_SOURCE_KEY);
};

/**
 * Get the return path based on stored navigation source
 * Falls back to "/" (the dashboard, which now serves as the family landing
 * screen) if no source is stored
 */
export const getReturnPath = (): string => {
  const source = getNavigationSource();
  return source?.path || "/";
};

/**
 * Check if we should redirect back to source (i.e., source exists)
 */
export const shouldRedirectToSource = (): boolean => {
  return getNavigationSource() !== null;
};

/**
 * Update the member ID in the stored navigation source path
 * Used when member is switched via profile picker to ensure back navigation goes to correct member
 */
export const updateNavigationSourceMemberId = (newMemberId: string): void => {
  const source = getNavigationSource();
  if (!source) return;

  // Update the memberId in the path: /profile/family/member/{memberId}
  const updatedPath = source.path.replace(
    /\/profile\/family\/member\/[^/?#]*/,
    `/profile/family/member/${newMemberId}`
  );

  // Save updated source
  setNavigationSource(source.type, updatedPath);
};
